"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UPDATE_DEMOGRAPHICS_REQUEST_EXAMPLE = exports.updateDemographicsRequestSchema = exports.updatePatientAddressInputSchema = void 0;
/**
 * `updateDemographics` — correcting a registered patient's record. Began as a
 * contract-first design (the contract was frozen before any implementation);
 * `UpdatePatientDemographicsService` now implements it. `expectedLockVersion`
 * is required — `Patient` has a real optimistic-lock column
 * (`docs/database/DATABASE-VERSIONING.md`), so any update contract touching it
 * must carry the version the client last read.
 */
const zod_1 = require("zod");
/**
 * WHAT `null` MEANS HERE. Every optional field is three-state: ABSENT leaves the
 * stored value alone, a VALUE replaces it, `null` CLEARS it. `.optional()`
 * alone cannot express the third state, and without it the desk could correct
 * a wrong marital status but never remove one entered by mistake.
 *
 * ADDITIVE. `salutationId` .. `address` were added after the contract was first
 * frozen (it named only the six core demographics); every field is optional and
 * the original shape still validates, so no existing caller is affected.
 */
const clearableId = zod_1.z.string().uuid().nullable().optional();
const clearableText = (max) => zod_1.z.string().trim().max(max).nullable().optional();
/** Where the patient lives. Replaces the CURRENT address; the superseded row is closed, never overwritten. */
exports.updatePatientAddressInputSchema = zod_1.z.object({
    addressLine1: zod_1.z.string().trim().min(1).max(255),
    addressLine2: clearableText(255),
    areaId: clearableId,
    countryId: zod_1.z.string().uuid(),
    stateId: clearableId,
    cityId: clearableId,
    foreignStateName: clearableText(150),
    foreignCityName: clearableText(150),
    postalCode: clearableText(20),
});
exports.updateDemographicsRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    expectedLockVersion: zod_1.z.number().int().nonnegative(),
    givenName: zod_1.z.string().trim().min(1).max(150).optional(),
    middleName: clearableText(150),
    familyName: clearableText(150),
    dateOfBirth: zod_1.z.coerce.date().nullable().optional(),
    dateOfBirthPrecision: zod_1.z.enum(['EXACT', 'YEAR_ONLY', 'ESTIMATED']).optional(),
    genderId: clearableId,
    maritalStatusId: clearableId,
    bloodGroupId: clearableId,
    religionId: clearableId,
    nationalityId: clearableId,
    salutationId: clearableId,
    qualificationId: clearableId,
    relationTypeId: clearableId,
    relationName: clearableText(255),
    /** The primary phone. Other phone numbers on file are left as they are. */
    mobile: zod_1.z
        .string()
        .trim()
        .regex(/^\+?[0-9 ()-]{5,20}$/, 'Enter a valid phone number')
        .nullable()
        .optional(),
    email: zod_1.z.string().trim().email().max(255).nullable().optional(),
    address: exports.updatePatientAddressInputSchema.optional(),
    /** Why the record was corrected — kept on the audit entry. */
    reason: zod_1.z.string().trim().max(500).optional(),
});
exports.UPDATE_DEMOGRAPHICS_REQUEST_EXAMPLE = {
    patientId: '22222222-2222-2222-2222-222222222222',
    expectedLockVersion: 3,
    familyName: 'Rao-Sharma',
};
//# sourceMappingURL=demographics.request.js.map